import { BrandMark, LanguageSelect } from '@ly360/ui';
import { AlertCircle, ArrowLeft, ArrowRight, Building2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import type { Locale } from '@ly360/i18n';
import { ApiError, type TenantChoice } from '../lib/api';
import { useAuth } from '../lib/auth';

/** Email-first sign-in: no workspace field. If the email belongs to several organisations, pick one. */
export function Login() {
  const { t, i18n } = useTranslation();
  const { session, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [choice, setChoice] = useState<TenantChoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [chosenLocale, setChosenLocale] = useState<Locale | undefined>(undefined);
  const ar = i18n.language === 'ar' || i18n.language === 'ur';

  if (session) return <Navigate to="/inbox" replace />;

  const run = async (tenant?: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await login(email.trim(), password, tenant, chosenLocale);
      setChoice(r);
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'rate_limited' ? t('auth.rateLimited') : t('auth.invalid'),
      );
      setChoice(null);
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run();
  };

  return (
    <div className="login">
      <section className="login-hero">
        <div className="brand" style={{ padding: 0 }}>
          <BrandMark size={48} />
          <span className="brand-name" style={{ fontSize: '1.5rem' }}>
            LY360
          </span>
        </div>
        <div className="login-hero-copy">
          <h1>
            {t('app.tagline')
              .split(/(?<=\.)\s/)
              .map((part, i, all) =>
                i === all.length - 1 ? (
                  <span key={i} className="grad-text">
                    {part}
                  </span>
                ) : (
                  <span key={i}>{part} </span>
                ),
              )}
          </h1>
          <p className="lead">{t('app.taglineLong')}</p>
        </div>
      </section>

      <section className="login-form-wrap">
        <div className="top">
          <LanguageSelect onChange={setChosenLocale} />
        </div>
        {choice ? (
          <div className="card login-form" data-testid="tenant-chooser">
            <h2>{t('auth.chooseTenant')}</h2>
            <p className="mute" style={{ marginTop: 0 }}>
              {t('auth.chooseTenantHint')}
            </p>
            <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem' }}>
              {choice.tenants.map((tn) => (
                <button
                  key={tn.slug}
                  className="btn btn-ghost btn-block"
                  style={{ justifyContent: 'flex-start' }}
                  disabled={busy}
                  onClick={() => void run(tn.slug)}
                >
                  <Building2 size={16} />
                  <span dir="auto">{ar && tn.nameAr ? tn.nameAr : tn.name}</span>
                </button>
              ))}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setChoice(null)}>
              <ArrowLeft size={14} className="icon-dir" />
              {t('auth.useDifferentEmail')}
            </button>
          </div>
        ) : (
          <form className="card login-form" onSubmit={submit} noValidate>
            <h2>{t('auth.title')}</h2>
            <p className="mute" style={{ marginTop: 0 }}>
              {t('auth.subtitle')}
            </p>
            {error && (
              <div className="form-error" role="alert">
                <AlertCircle size={16} style={{ flex: 'none', marginTop: 2 }} />
                {error}
              </div>
            )}
            <div className="field">
              <label htmlFor="email">{t('auth.email')}</label>
              <input
                id="email"
                className="input"
                type="email"
                dir="ltr"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="password">{t('auth.password')}</label>
              <input
                id="password"
                className="input"
                type="password"
                dir="ltr"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button className="btn btn-grad btn-block" type="submit" disabled={busy || !email || !password}>
              {busy ? t('auth.submitting') : t('auth.submit')}
              <ArrowRight size={16} className="arw icon-dir" />
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
